import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, Phone, MapPin, Calendar, Home, ShoppingBag, Users, Tool, Edit3,
  Play, PauseCircle, RefreshCw, CheckCircle, Lock, Link2, AlertTriangle,
} from "react-feather";
import toast from "react-hot-toast";
import SectionCard from "../../../components/common/SectionCard";
import CompactInfoBlock, { CompactInfoField } from "../../../components/common/CompactInfoBlock";
import StatusBadge from "../../../components/common/StatusBadge";
import TechnicianTile from "../components/TechnicianTile";
import ServiceReportPanel from "../components/ServiceReportPanel";
import DiagnosisPanel from "../components/DiagnosisPanel";
import ActivityTimeline from "../components/ActivityTimeline";
import CustomerFeedbackPanel from "../../serviceFeedback/components/CustomerFeedbackPanel";
import AssignTechniciansModal from "../components/AssignTechniciansModal";
import StartWorkModal from "../components/StartWorkModal";
import EditDescriptionModal from "../components/EditDescriptionModal";
import RemarksActionModal from "../components/RemarksActionModal";
import LogWorkModal from "../components/LogWorkModal";
import {
  useGetTechnicalWorkItemQuery,
  useResumeTechnicalWorkMutation,
  useHoldTechnicalWorkMutation,
  useCompleteTechnicalWorkMutation,
  useCloseTechnicalWorkMutation,
} from "../technicalApi";
import { formatElapsedSince, formatTurnaroundDays, finishedDateOf, elapsedFromOf } from "../workElapsed";
import {
  useCanManageWork, useCanCloseWork, useCanEditComplaint, useCanViewActivity, useCanCreateServiceVisit,
} from "../useTechnicalPermissions";
import {
  TECHNICAL_STATUS_CLASS, TECHNICAL_STATUS_LABEL, TECHNICAL_SOURCE_LABEL,
  TECHNICAL_SOURCE_CLASS, TECHNICAL_DESCRIPTION_LABEL, isFinalized,
} from "../technicalStatusMaps";
import { MAROON } from "../../../components/common/brandColors";
import { paths } from "../../../routes/paths";

const formatDay = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null;

// Age and turnaround both come from features/technical/workElapsed.js now -
// one copy shared with the work list and the pending-assignment queue.

// The manager's screen. Every status guard below mirrors the proc that
// actually enforces it, so the UI never offers a button the server refuses:
//   Start    NOT_STARTED only          (sp_..._start,    THROW 51102)
//   Hold     ONGOING or ASSIGNED       (sp_..._hold,     THROW 51104)
//   Resume   ON_HOLD only              (sp_..._resume,   THROW 51105)
//   Complete ONGOING or ASSIGNED       (sp_..._complete, THROW 51106)
//   Close    COMPLETED only            (sp_..._close,    THROW 51107)
// and COMPLETED/CLOSED freeze assignment and description edits entirely
// (TechnicalService.EnsureNotFinalized) - nothing may change between the
// technician reporting done and the admin verifying it.
const WorkDetail = () => {
  const { recordGuid } = useParams();
  const canManage = useCanManageWork();
  const canClose = useCanCloseWork();
  const canEditDescription = useCanEditComplaint();
  const canSeeHistory = useCanViewActivity();
  const canCreateServiceVisit = useCanCreateServiceVisit();

  const { data, isLoading, isError, error } = useGetTechnicalWorkItemQuery(recordGuid);
  const [resumeWork, { isLoading: isResuming }] = useResumeTechnicalWorkMutation();
  const [holdWork, { isLoading: isHolding }] = useHoldTechnicalWorkMutation();
  const [completeWork, { isLoading: isCompleting }] = useCompleteTechnicalWorkMutation();
  const [closeWork, { isLoading: isClosing }] = useCloseTechnicalWorkMutation();

  const [showAssign, setShowAssign] = useState(false);
  const [showStart, setShowStart] = useState(false);
  const [showEditDescription, setShowEditDescription] = useState(false);
  const [showHold, setShowHold] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [showFollowUp, setShowFollowUp] = useState(false);

  if (isLoading) {
    return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;
  }

  if (isError || !data?.work) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Link to={paths.technicalWork} className="d-inline-flex align-items-center mb-3">
            <ArrowLeft size={16} className="me-1" /> Back to Technical Work
          </Link>
          <div className="alert alert-danger">
            Could not load this work item ({error?.message || "server error"}).
          </div>
        </div>
      </div>
    );
  }

  const { work, technicians = [], diagnoses = [], serviceReport, serviceReportItems = [] } = data;
  const frozen = isFinalized(work.status);

  // SALES_ORDER only: sp_app_technical_work_start refuses anything else
  // (THROW 51102) because a Complaint/Service Visit "starts" by assigning a
  // technician, which flips it straight to ASSIGNED. Without this check the
  // button showed on every NOT_STARTED complaint and service visit and failed
  // every time it was pressed. Flutter already hid it; React had not.
  const canStart = canManage && work.status === "NOT_STARTED" && work.sourceType === "SALES_ORDER";
  const canHold = canManage && (work.status === "ONGOING" || work.status === "ASSIGNED");
  const canResume = canManage && work.status === "ON_HOLD";
  const canComplete = canManage && (work.status === "ONGOING" || work.status === "ASSIGNED");
  const canVerifyClose = canClose && work.status === "COMPLETED";
  const canAssign = canManage && !frozen;
  const canEditThis = canEditDescription && !frozen && work.sourceType !== "SALES_ORDER";

  // Same two figures the work list's pill shows, from the same helper, so a job
  // never reads one age in the list and another on its own screen.
  const turnaround = formatTurnaroundDays(work.createdDate, finishedDateOf(work));
  const openFor = formatElapsedSince(elapsedFromOf(work));

  const handleResume = async () => {
    try {
      await resumeWork(recordGuid).unwrap();
      toast.success("Work resumed");
    } catch (e) {
      toast.error(e?.message || "Could not resume this work");
    }
  };

  const handleHold = async ({ date, remarks }) => {
    try {
      await holdWork({ recordGuid, holdDate: date, holdRemarks: remarks }).unwrap();
      toast.success("Work put on hold");
      setShowHold(false);
    } catch (e) {
      toast.error(e?.message || "Could not hold this work");
    }
  };

  const handleComplete = async ({ date, remarks }) => {
    try {
      await completeWork({ recordGuid, completedDate: date, completedRemarks: remarks }).unwrap();
      toast.success("Work completed");
      setShowComplete(false);
    } catch (e) {
      // A Service Visit is refused here until every active checklist item has
      // a value - the server names the missing ones, so surface its message
      // rather than a generic one.
      toast.error(e?.message || "Could not complete this work");
    }
  };

  const handleClose = async ({ date, remarks, customerComment }) => {
    try {
      await closeWork({ recordGuid, closedDate: date, closedRemarks: remarks, customerComment }).unwrap();
      toast.success("Work verified and closed");
      setShowClose(false);
    } catch (e) {
      toast.error(e?.message || "Could not close this work");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <Link to={paths.technicalWork} className="d-inline-flex align-items-center mb-2">
          <ArrowLeft size={16} className="me-1" /> Back to Technical Work
        </Link>

        {/* Gradient header block, house style - the entity reads as a real
            header rather than another card in the stack. */}
        <div
          className="p-3 mb-3 rounded"
          style={{
            background: `linear-gradient(135deg, ${MAROON}1A 0%, ${MAROON}05 100%)`,
            border: `1px solid ${MAROON}33`,
          }}
        >
          <div className="d-flex flex-wrap justify-content-between align-items-start">
            <div className="me-3">
              <h4 className="mb-1" style={{ color: MAROON, fontWeight: 800 }}>{work.workNumber}</h4>
              <div className="fw-semibold" style={{ fontSize: 15 }}>{work.customerName}</div>
            </div>
            <div className="text-end">
              <StatusBadge status={work.status} classMap={TECHNICAL_STATUS_CLASS} labelMap={TECHNICAL_STATUS_LABEL} />
              <span className={`badge ms-2 ${TECHNICAL_SOURCE_CLASS[work.sourceType] || "badge-linesecondary"}`}>
                {TECHNICAL_SOURCE_LABEL[work.sourceType] || work.sourceType}
              </span>
              <div
                className="mt-1"
                style={{
                  fontSize: 12,
                  fontWeight: !frozen && openFor?.isLong ? 700 : 400,
                  color: !frozen && openFor?.isLong ? "#C62828" : "#6c757d",
                }}
              >
                {frozen
                  ? turnaround && `${turnaround} turnaround`
                  : openFor && `Open ${openFor.label}`}
              </div>
            </div>
          </div>

          {/* One wrapped block instead of a stacked row per short field -
              standing project requirement for information density. */}
          <CompactInfoBlock className="mt-3">
            <CompactInfoField label="Phone" value={work.customerPhone} icon={Phone} />
            <CompactInfoField label="Address" value={work.customerAddress} icon={MapPin} />
            <CompactInfoField label="Branch" value={work.branchCode} icon={Home} />
            <CompactInfoField label="Order" value={work.orderNumber} icon={ShoppingBag} />
            <CompactInfoField label="Logged" value={formatDay(work.createdDate)} icon={Calendar} />
            <CompactInfoField label="Expected" value={formatDay(work.expectedCompletionDate)} icon={Calendar} />
            <CompactInfoField label="Started" value={formatDay(work.startedDate)} icon={Calendar} />
            <CompactInfoField label="Completed" value={formatDay(work.completedDate)} icon={Calendar} />
            <CompactInfoField label="Closed" value={formatDay(work.closedDate)} icon={Calendar} />
          </CompactInfoBlock>

          {work.linkedFromWorkNumber && (
            <div className="mt-2 d-inline-flex align-items-center text-muted" style={{ fontSize: 12.5 }}>
              <Link2 size={13} className="me-1" />
              Follow-up to <strong className="ms-1">{work.linkedFromWorkNumber}</strong>
            </div>
          )}
        </div>

        {/* Action bar */}
        {(canStart || canHold || canResume || canComplete || canVerifyClose || canAssign) && (
          <div className="d-flex flex-wrap gap-2 mb-3">
            {canStart && (
              <button type="button" className="btn btn-primary" onClick={() => setShowStart(true)}>
                <Play size={15} className="me-1" /> Start Work
              </button>
            )}
            {canResume && (
              <button type="button" className="btn btn-primary" onClick={handleResume} disabled={isResuming}>
                <RefreshCw size={15} className="me-1" /> {isResuming ? "Resuming..." : "Resume"}
              </button>
            )}
            {canHold && (
              <button type="button" className="btn btn-warning" onClick={() => setShowHold(true)}>
                <PauseCircle size={15} className="me-1" /> Put On Hold
              </button>
            )}
            {canComplete && (
              <button type="button" className="btn btn-success" onClick={() => setShowComplete(true)}>
                <CheckCircle size={15} className="me-1" /> Mark Completed
              </button>
            )}
            {canVerifyClose && (
              <button type="button" className="btn btn-dark" onClick={() => setShowClose(true)}>
                <Lock size={15} className="me-1" /> Verify &amp; Close
              </button>
            )}
            {canAssign && (
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowAssign(true)}>
                <Users size={15} className="me-1" /> Manage Technicians
              </button>
            )}
          </div>
        )}

        {frozen && (
          <div className="alert alert-secondary py-2 px-3 d-flex align-items-center">
            <Lock size={15} className="me-2 flex-shrink-0" />
            <small className="mb-0">
              This job is {work.status === "CLOSED" ? "closed" : "completed and awaiting verification"} and is frozen -
              technicians, findings and the service report can no longer change. A recurrence is raised as a new linked
              service visit, never by reopening this one.
            </small>
          </div>
        )}

        <div className="row">
          <div className="col-lg-7">
            <SectionCard
              icon={Edit3}
              title={TECHNICAL_DESCRIPTION_LABEL[work.sourceType] || "Description"}
              right={
                canEditThis && (
                  <button type="button" className="btn btn-sm btn-link p-0" onClick={() => setShowEditDescription(true)}>
                    Edit wording
                  </button>
                )
              }
            >
              <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                {work.complaintDescription || <span className="text-muted">Nothing recorded.</span>}
              </p>
            </SectionCard>

            <ServiceReportPanel report={serviceReport} items={serviceReportItems} sourceType={work.sourceType} />

            {/* Sits directly under the service report on purpose: the report is what WE
                say happened, this is what the CUSTOMER says about it. Renders nothing when
                no email has gone out for this job, so older work is unaffected. */}
            <CustomerFeedbackPanel sourceRecordGuid={recordGuid} />

            <SectionCard icon={Tool} title="Technician's Findings">
              <DiagnosisPanel diagnoses={diagnoses} />
            </SectionCard>
          </div>

          <div className="col-lg-5">
            <SectionCard
              icon={Users}
              title="Technicians"
              right={<span className="badge badge-linesecondary">{technicians.length}</span>}
            >
              {technicians.length === 0 ? (
                <div className="d-flex align-items-center text-muted">
                  <AlertTriangle size={15} className="me-2 flex-shrink-0" />
                  <span style={{ fontSize: 13 }}>Nobody is assigned to this job yet.</span>
                </div>
              ) : (
                technicians.map((t) => <TechnicianTile key={t.recordGuid} technician={t} />)
              )}
            </SectionCard>

            {(work.onHoldRemarks || work.startedRemarks || work.completedRemarks || work.closedRemarks) && (
              <SectionCard icon={CheckCircle} title="Outcome">
                {work.startedRemarks && (
                  <div className="mb-2">
                    <small className="text-muted d-block">On starting &middot; {work.startedByName || "-"}</small>
                    <div style={{ fontSize: 13.5 }}>{work.startedRemarks}</div>
                  </div>
                )}
                {work.onHoldRemarks && (
                  <div className="mb-2">
                    <small className="text-muted d-block">Hold reason &middot; {work.onHoldByName || "-"}</small>
                    <div style={{ fontSize: 13.5 }}>{work.onHoldRemarks}</div>
                  </div>
                )}
                {work.completedRemarks && (
                  <div className="mb-2">
                    <small className="text-muted d-block">Work done &middot; {work.completedByName || "-"}</small>
                    <div style={{ fontSize: 13.5 }}>{work.completedRemarks}</div>
                  </div>
                )}
                {work.closedRemarks && (
                  <div className="mb-0">
                    <small className="text-muted d-block">Verified &middot; {work.closedByName || "-"}</small>
                    <div style={{ fontSize: 13.5 }}>{work.closedRemarks}</div>
                  </div>
                )}
              </SectionCard>
            )}

            {/* Supervisory only - the endpoint itself is gated
                Technical.ManageWork|Technical.CloseWork, so a technician-shaped
                login never sees this card at all. */}
            {canSeeHistory && <ActivityTimeline recordGuid={recordGuid} />}

            {frozen && canCreateServiceVisit && (
              <SectionCard icon={RefreshCw} title="Problem persists?">
                <p className="text-muted mb-2" style={{ fontSize: 13 }}>
                  Raise a new service visit for the same customer, linked to {work.workNumber}, so this record stays
                  exactly as it was verified. One work item can only ever hold one service report, so reopening would
                  overwrite the first visit's readings.
                </p>
                <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => setShowFollowUp(true)}>
                  Raise Follow-Up Visit
                </button>
              </SectionCard>
            )}
          </div>
        </div>
      </div>

      <AssignTechniciansModal show={showAssign} onClose={() => setShowAssign(false)} work={work} technicians={technicians} />
      <StartWorkModal show={showStart} onClose={() => setShowStart(false)} work={work} technicians={technicians} />
      <EditDescriptionModal show={showEditDescription} onClose={() => setShowEditDescription(false)} work={work} />

      <RemarksActionModal
        show={showHold}
        onClose={() => setShowHold(false)}
        onConfirm={handleHold}
        isSaving={isHolding}
        title="Put Work On Hold"
        dateLabel="Hold date"
        remarksLabel="Why is it on hold"
        remarksPlaceholder="e.g. waiting for a replacement panel"
        confirmLabel="Put On Hold"
        confirmVariant="warning"
      />
      <RemarksActionModal
        show={showComplete}
        onClose={() => setShowComplete(false)}
        onConfirm={handleComplete}
        isSaving={isCompleting}
        title="Mark Completed"
        dateLabel="Completion date"
        remarksLabel="What was done"
        remarksPlaceholder="Summary of the work carried out on site"
        confirmLabel="Mark Completed"
        confirmVariant="success"
        intro={
          work.sourceType === "SERVICE_VISIT"
            ? "A service visit is the AMC service, so it cannot be completed until every active checklist item has a value."
            : undefined
        }
      />
      <RemarksActionModal
        show={showClose}
        onClose={() => setShowClose(false)}
        onConfirm={handleClose}
        isSaving={isClosing}
        title="Verify & Close"
        dateLabel="Closing date"
        remarksLabel="Verification remarks"
        remarksPlaceholder="What you checked before signing this off"
        confirmLabel="Verify & Close"
        confirmVariant="dark"
        withCustomerComment
        intro="The final step, after the technician has reported the job done. Once closed, nothing on this record can change."
      />

      <LogWorkModal
        show={showFollowUp}
        sourceType="SERVICE_VISIT"
        onClose={() => setShowFollowUp(false)}
        linkedFromWorkRecordGuid={work.recordGuid}
        linkedFromWorkNumber={work.workNumber}
      />
    </div>
  );
};

export default WorkDetail;
